
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PromptsTable } from "./PromptsTable";
import { PromptEditor } from "./PromptEditor";
import { AlertCircle, Plus } from "lucide-react";
import { Agent } from "@/types/agent";
import { AgentSelect } from "./AgentSelect";
import { useAgents } from "@/hooks/useAgents";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CustomMessagesPanel } from "./CustomMessagesPanel";
import { ChatPanel } from "./ChatPanel";
import { MainEditPanel } from "./MainEditPanel";
import { useToast } from "@/hooks/use-toast";

type Attachment = {
  name: string;
  url: string;
  type: string;
};

export const PromptsSettings = () => {
  const [selectedTab, setSelectedTab] = useState("edit");
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [customPrompt, setCustomPrompt] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { agents, isLoading: isLoadingAgents } = useAgents();
  const { toast } = useToast();

  // Carregar prompt personalizado, se existir
  useEffect(() => {
    const loadCustomPrompt = async () => {
      if (!selectedAgent) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase
          .from('agent_custom_prompts')
          .select('prompt')
          .eq('agent_id', selectedAgent.id)
          .maybeSingle();
          
        if (error) throw error;
        
        if (data) {
          setCustomPrompt(data.prompt);
          setIsEditing(true);
        } else {
          setCustomPrompt(selectedAgent.defaultPrompt || "");
          setIsEditing(false);
        }
      } catch (err: any) {
        console.error("Erro ao carregar prompt personalizado:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadCustomPrompt();
  }, [selectedAgent]);

  const handleAgentChange = async (agent: Agent | null) => {
    setSelectedAgent(agent);
    if (!agent) {
      setCustomPrompt("");
      setIsEditing(false);
    }
  };

  const handlePromptChange = (value: string) => {
    setCustomPrompt(value);
  };

  const savePrompt = async () => {
    if (!selectedAgent) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (isEditing) {
        // Atualizar prompt existente
        const { error } = await supabase
          .from('agent_custom_prompts')
          .update({ 
            prompt: customPrompt,
            updated_at: new Date()
          })
          .eq('agent_id', selectedAgent.id);
          
        if (error) throw error;
        
        toast({
          title: "Prompt atualizado",
          description: "O prompt do agente foi atualizado com sucesso."
        });
      } else {
        // Criar novo prompt personalizado
        const { error } = await supabase
          .from('agent_custom_prompts')
          .insert([{
            agent_id: selectedAgent.id,
            prompt: customPrompt,
            created_at: new Date(),
            updated_at: new Date()
          }]);
          
        if (error) throw error;
        
        setIsEditing(true);
        toast({
          title: "Prompt criado",
          description: "O prompt personalizado foi criado com sucesso."
        });
      }
    } catch (err: any) {
      console.error("Erro ao salvar prompt:", err);
      setError(err.message);
      toast({
        variant: "destructive",
        title: "Erro",
        description: `Não foi possível salvar o prompt: ${err.message}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetPrompt = () => {
    if (!selectedAgent) return;
    setCustomPrompt(selectedAgent.defaultPrompt || "");
    toast({
      title: "Prompt redefinido",
      description: "O prompt foi redefinido para o padrão do agente."
    });
  };

  const deleteCustomPrompt = async () => {
    if (!selectedAgent || !isEditing) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase
        .from('agent_custom_prompts')
        .delete()
        .eq('agent_id', selectedAgent.id);
        
      if (error) throw error;
      
      setIsEditing(false);
      setCustomPrompt(selectedAgent.defaultPrompt || "");
      toast({
        title: "Prompt removido",
        description: "O prompt personalizado foi removido com sucesso."
      });
    } catch (err: any) {
      console.error("Erro ao excluir prompt:", err);
      setError(err.message);
      toast({
        variant: "destructive",
        title: "Erro",
        description: `Não foi possível excluir o prompt: ${err.message}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Função adaptadora para compatibilidade com o componente de upload de anexos
  const handleAttachmentUpload = (_attachments?: Attachment[]) => {
    // Esta função não faz nada, apenas existe para compatibilidade de tipo
    return Promise.resolve();
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Configuração de Prompts</h3>
        <p className="text-sm text-muted-foreground">
          Personalize as instruções para seus agentes de IA.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <CardTitle>Agentes</CardTitle>
                <CardDescription>
                  Selecione um agente para editar seu prompt
                </CardDescription>
              </div>
              <AgentSelect 
                agents={agents || []}
                selectedAgent={selectedAgent}
                onAgentChange={handleAgentChange}
                isLoading={isLoadingAgents}
              />
            </div>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Erro</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {selectedAgent ? (
              <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="edit">Editar Prompt</TabsTrigger>
                  <TabsTrigger value="messages">Mensagens</TabsTrigger>
                  <TabsTrigger value="test">Testar</TabsTrigger>
                </TabsList>

                <TabsContent value="edit">
                  <MainEditPanel 
                    isEditing={isEditing}
                    customPrompt={customPrompt}
                    handlePromptChange={handlePromptChange}
                    selectedAgent={selectedAgent}
                    savePrompt={savePrompt}
                    resetPrompt={resetPrompt}
                    deleteCustomPrompt={deleteCustomPrompt}
                    isLoading={isLoading}
                  />
                </TabsContent>

                <TabsContent value="messages">
                  <CustomMessagesPanel 
                    selectedAgent={selectedAgent}
                    agentId={selectedAgent.id}
                  />
                </TabsContent>

                <TabsContent value="test">
                  <ChatPanel 
                    selectedAgent={selectedAgent}
                    customPrompt={customPrompt}
                    onUploadAttachment={handleAttachmentUpload}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  Selecione um agente para editar seu prompt
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle>Prompt Templates</CardTitle>
              <CardDescription>
                Templates de prompts pré-definidos para diferentes casos de uso
              </CardDescription>
            </div>
            <Button variant="outline" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Novo Template
            </Button>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6">
            <PromptsTable onSelectPrompt={setSelectedPrompt} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
